"use client";
import { useEffect, useState, type FormEvent } from "react";
import Link from "next/link";
import { PLAYBOOK } from "@/core/commerce/catalog";
export async function commerceRequest(
  operation: string,
  body: Record<string, unknown> = {},
) {
  const r = await fetch(`/api/commerce/${operation}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const data = await r.json();
  if (!r.ok) throw new Error(data.error ?? "Please try again.");
  return data;
}
export function CheckoutForm() {
  const [offer, setOffer] = useState<{
    available: boolean;
    price: number | null;
    currency?: string;
    testMode: boolean;
  } | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    void commerceRequest("offer")
      .then(setOffer)
      .catch(() =>
        setOffer({ available: false, price: null, testMode: false }),
      );
  }, []);
  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (busy) return;
    const f = new FormData(e.currentTarget);
    setBusy(true);
    setError("");
    try {
      const result = await commerceRequest("checkout", {
        email: f.get("email"),
        acceptedTerms: f.get("terms") === "on",
        marketingConsent: f.get("updates") === "on",
      });
      window.location.assign(result.url);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Please try again.");
      setBusy(false);
    }
  }
  return (
    <form onSubmit={submit} className="co-checkout" aria-busy={busy}>
      <div className="co-price">
        <span>
          {new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: offer?.currency ?? PLAYBOOK.currency,
            maximumFractionDigits: 0,
          }).format((offer?.price ?? PLAYBOOK.priceCents) / 100)}
        </span>
        <small>One payment. Yours to put to work.</small>
      </div>
      {offer?.testMode && (
        <p className="co-notice" role="status">
          Test checkout — no real payment will be collected.
        </p>
      )}
      <label htmlFor="purchase-email">Where should we send your kit?</label>
      <input
        id="purchase-email"
        name="email"
        type="email"
        autoComplete="email"
        maxLength={254}
        placeholder="you@company.com"
        required
        disabled={busy}
      />
      <label className="co-checkbox">
        <input name="updates" type="checkbox" disabled={busy} />
        <span>
          Email me practical workflows and an invitation to apply for private
          Haulbase access. I can unsubscribe anytime.
        </span>
      </label>
      <label className="co-checkbox">
        <input name="terms" type="checkbox" required disabled={busy} />
        <span>
          I agree to the <Link href="/legal/playbook">purchase terms</Link>.
          This purchase is the digital kit; software access requires separate
          approval.
        </span>
      </label>
      {error && (
        <p className="co-error" role="alert">
          {error}
        </p>
      )}
      <button className="co-button" disabled={busy || !offer?.available}>
        {busy
          ? "Opening secure checkout…"
          : !offer
            ? "Checking availability…"
            : offer.available
              ? "Get the playbook  ↗"
              : "Checkout opens soon"}
      </button>
      {offer && !offer.available && (
        <p className="co-fine" role="status">
          We’re preparing the final release. Explore the sample workflow, or{" "}
          <a href="mailto:jerrod.a@haulbase.ai">contact us</a> about availability.
        </p>
      )}
      <p className="co-fine">
        Secure hosted checkout · Immediate digital delivery after payment · No
        subscription
      </p>
    </form>
  );
}
